import { setup } from 'xstate5';

import {
  type JoinOrSignInStateMachineContext,
  type JoinOrSignInStateMachineEvents,
} from './join-or-sign-in-machine.types';
import { Actions } from './setup/join-or-sign-in-machine.actions';
import { Actors } from './setup/join-or-sign-in-machine.actors';
import { initialContext } from './setup/join-or-sign-in-machine.context';
import { Delays } from './setup/join-or-sign-in-machine.delays';
import { Guards } from './setup/join-or-sign-in-machine.guards';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    context: {} as JoinOrSignInStateMachineContext,
    events: {} as JoinOrSignInStateMachineEvents,
  },
  actions: Actions,
  actors: Actors,
  guards: Guards,
  delays: Delays,
});

// ─────────────────────────────────────────────────────────────────────────────

export const joinOrSignInStateMachine = model.createMachine({
  id: 'join-or-sign-in-state-machine',
  context: initialContext,
  initial: 'waiting',
  states: {
    waiting: {
      entry: ['removeRefreshToken'], // NOTE: Remove any stale refresh token
      on: {
        /**
         * When a user reloads the app after completing the Azure user verification phase,
         * the hosted frame will have the preserved session from an already existing account.
         * In that situation, we recover the authentication state so that the user can
         * proceed from the previous point.
         */
        TOKEN_RECEIVED: {
          target: 'signing-in',
          actions: ['storeToken', 'storeEmail'],
        },
        AUTH_ERROR: {
          target: 'authentication-failed',
          actions: [{ type: 'onInitError', params: ({ event }) => ({ data: event.data }) }],
        },
      },
      after: {
        EMBEDDED_FRAME_LOADING_TIMEOUT: {
          target: 'authentication-failed',
          actions: ['onInitTimeoutError'],
        },
      },
    },
    'entering-email-address': {
      on: {
        LOG_IN: {
          target: 'verification-code-loading',
        },

        // NOTE: Playwright and other test runners are able to hit the
        //       "Continue" button even when the relevant listener is not
        //       even registered.
        //
        //       We listen to the `VERIFICATION_CODE_LOADED` event in case
        //       the initial click was not handled and the user is already on
        //       the next screen to better manage such circumstances.
        VERIFICATION_CODE_LOADED: {
          target: 'entering-verification-code',
          actions: ['storeEmail'],
        },
      },
    },
    'verification-code-loading': {
      on: {
        VERIFICATION_CODE_LOADED: {
          target: 'entering-verification-code',
          actions: ['storeEmail'],
        },
        ERROR: {
          target: 'entering-email-address',
          actions: [{ type: 'onErrorMessage', params: ({ event }) => ({ text: event.text }) }],
        },
      },
      after: {
        EMBEDDED_FRAME_LOADING_TIMEOUT: {
          target: 'authentication-failed',
          actions: ['onVerificationScreenLoadingTimeout'],
        },
      },
    },
    'entering-verification-code': {
      on: {
        VERIFY_CODE: {
          target: 'verification',
        },
        ERROR: {
          actions: [{ type: 'onErrorMessage', params: ({ event }) => ({ text: event.text }) }],
        },
        SUCCESS: {
          actions: [{ type: 'onSuccessMessage', params: ({ event }) => ({ text: event.text }) }],
        },
      },
    },
    verification: {
      on: {
        ERROR: {
          target: 'entering-verification-code',
          actions: [{ type: 'onErrorMessage', params: ({ event }) => ({ text: event.text }) }],
        },
        SUCCESS: {
          actions: [{ type: 'onSuccessMessage', params: ({ event }) => ({ text: event.text }) }],
        },
        TOKEN_RECEIVED: {
          target: 'signing-in',
          actions: ['storeToken'],
        },
      },
      after: {
        EMBEDDED_FRAME_LOADING_TIMEOUT: {
          target: 'authentication-failed',
          actions: ['onVerificationTimeout'],
        },
      },
    },
    'signing-in': {
      invoke: {
        id: 'signIn',
        src: 'signIn',
        input: ({ context }) => ({ accessToken: context.accessToken }),
        onDone: [
          {
            target: 'redirect',
            guard: { type: 'checkIfSignedIn', params: ({ event }) => event.output },
          },
          {
            target: 'filling-account-data',
          },
        ],
        onError: {
          target: 'authentication-failed',
          actions: [
            { type: 'onSignInSystemError', params: ({ event }) => ({ error: event.error }) },
          ],
        },
      },
    },
    'filling-account-data': {
      on: {
        SUBMIT_ACCOUNT_DATA: [
          {
            guard: 'checkIfNeedToRefreshTokens',
            target: 'refreshing-access-tokens',
            actions: ['storeAccountInput'],
          },
          {
            target: 'creating-account',
            actions: ['storeAccountInput'],
          },
        ],
        RESTART: 'waiting',
      },
    },
    'refreshing-access-tokens': {
      description:
        'A state in which a machine attempts to refresh access tokens using the stored refresh token.',
      invoke: {
        id: 'refreshAccessTokens',
        src: 'refreshAccessTokens',
        input: ({ context }) => ({ refreshToken: context.refreshToken }),
        onDone: {
          target: 'creating-account',
          actions: [
            { type: 'storeAccessTokens', params: ({ event }) => ({ output: event.output }) },
          ],
        },
        onError: {
          target: 'authentication-failed',
          actions: [
            {
              type: 'onRefreshAccessTokensSystemError',
              params: ({ event }) => ({ error: event.error }),
            },
          ],
        },
      },
    },
    'creating-account': {
      invoke: {
        id: 'createAccount',
        src: 'createAccount',
        input: ({ context }) => ({
          email: context.email,
          accessToken: context.accessToken,
          createAccountInput: context.createAccountInput,
        }),
        onDone: [
          {
            target: 'redirect',
            guard: { type: 'checkIfSignedUp', params: ({ event }) => event.output },
          },
          {
            target: 'filling-account-data',
          },
        ],
        onError: {
          target: 'filling-account-data',
          actions: [
            { type: 'onCreateAccountSystemError', params: ({ event }) => ({ error: event.error }) },
          ],
        },
      },
    },
    'authentication-failed': {
      on: {
        RESTART: 'waiting',
      },
    },
    redirect: {
      entry: ['storeTokens'],
      on: {
        RESTART: 'waiting',
      },
    },
  },
  on: {
    DEBUG_INFO: {
      actions: [{ type: 'onDebugMessage', params: ({ event }) => ({ data: event.data }) }],
    },

    /**
     * When a customer uses the back button on a Web platform, the browser
     * navigates within the 'iframe' rather than to the previous page in
     * the parent app.
     *
     * This can cause a stuck condition and lead to a timeout.
     *
     * To prevent this, we listen globally for the "Email"
     * template loading completion event and restart both the embedded
     * flow and the state machine, allowing for email changes or/and a
     * complete restart.
     */
    EMAIL_ADDRESS_LOADED: {
      target: '.entering-email-address',
    },
  },
});
